import { GROUP_BY_CONFIG } from 'components/HeatmapChart/groupByConfig';
import ApplicationModel, { attr } from '../ApplicationModel';
import DateRangeFilter, {
  type DateRangeFilterValue,
} from '../filters/DateRangeFilter';
import RecordTypeFilter from '../filters/RecordTypeFilter';
import StatusRangeFilter, {
  type StatusRangeFilterValue,
} from '../filters/StatusRangeFilter';
import ValueFilter from '../filters/ValueFilter';
import { model } from '../modelBuilder';
import Query from '../Query';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import { type BookmarksHeatmapChartAttributes } from './generated/BookmarksHeatmapChartAttributes';
import {
  type BookmarksHeatmapChartDataRow,
  type BookmarksHeatmapChartGrouping_value,
} from './generated/types';

type Filters = {
  groupBy: ValueFilter<BookmarksHeatmapChartGrouping_value>;
  recordType: RecordTypeFilter;
  statusRange: StatusRangeFilter;
  dateRange: DateRangeFilter;
};

type FilterValues = {
  groupBy?: BookmarksHeatmapChartGrouping_value;
  recordType?: string[];
  statusRange?: StatusRangeFilterValue;
  dateRange?: DateRangeFilterValue;
};

export type FiltersScope = keyof Filters;

interface BookmarksHeatmapChart
  extends BookmarksHeatmapChartAttributes,
    HasBookmarkFilters<Filters> {
  filters: FilterValues;
  filterObjects: Filters;
}

@model
@HasBookmarkFilters
class BookmarksHeatmapChart extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksHeatmapChart' = 'BookmarksHeatmapChart';
  get typename(): 'BookmarksHeatmapChart' {
    return 'BookmarksHeatmapChart';
  }

  static internalTypename: 'Bookmarks::HeatmapChart' =
    'Bookmarks::HeatmapChart';
  get internalTypename(): 'Bookmarks::HeatmapChart' {
    return 'Bookmarks::HeatmapChart';
  }

  static bookmarkName = 'Bookmarks::HeatmapChart';

  static inputType = 'HeatmapChartAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
    chartData: attr.hasMany({
      args: {
        grouping: 'BookmarksHeatmapChartGrouping',
        dateFrom: 'ISO8601DateTime',
        dateTo: 'ISO8601DateTime',
        startStatusId: 'ID!',
        endStatusId: 'ID!',
        subjectType: 'BookmarksHeatmapChartSubjectType',
      },
      readable: true,
    }),
  };
  // End auto-generated

  static DEFAULT_GROUPING: BookmarksHeatmapChartGrouping_value = 'ASSIGNED_TO';

  initialize() {
    this.local = { filterObjects: {} };
    this.filters = this.filters || {};
  }

  async createFilters(defaults?: {
    statusRange?: StatusRangeFilterValue;
    dateRange?: DateRangeFilterValue;
  }): Promise<Filters> {
    return {
      groupBy: new ValueFilter<BookmarksHeatmapChartGrouping_value>(
        this.filters.groupBy && this.filters.groupBy in GROUP_BY_CONFIG
          ? this.filters.groupBy
          : BookmarksHeatmapChart.DEFAULT_GROUPING
      ),
      recordType: new RecordTypeFilter({
        initialItems: this.filters.recordType ?? [],
        recordTypes: ['Feature', 'Requirement'],
      }),
      statusRange: new StatusRangeFilter(
        this.filters.statusRange ?? defaults?.statusRange ?? {}
      ),
      dateRange: new DateRangeFilter(
        this.filters.dateRange ??
          defaults?.dateRange ?? {
            start: '',
            end: '',
          },
        false
      ),
    };
  }

  async fetchChartData(args: {
    grouping?: BookmarksHeatmapChartGrouping_value;
    subjectType?: string;
    statusPositionStart?: number;
    statusPositionEnd?: number;
    dateFrom?: string | Date;
    dateTo?: string | Date;
  }): Promise<BookmarksHeatmapChartDataRow[]> {
    const params = Object.fromEntries(
      Object.entries(args).filter(([, v]) => !!v)
    );

    const scope = BookmarksHeatmapChart.merge({
      chartData: new Query()
        .select([
          'workflowName',
          'workflowPosition',
          'sum',
          'groupByValue',
          'valuePosition',
          'metadata',
        ])
        .merge({
          records: [
            'id',
            'type',
            'duration', // todo, including for debugging, remove once verified
          ],
        })
        .argument(params),
    });

    const data = await scope.find(this.id);

    return data?.chartData;
  }
}

export default BookmarksHeatmapChart;
